import { useState } from 'react';
import { useApp } from '@/context/AppContext';

function AddEmployeeModal({ onClose }) {
  const { addEmployee } = useApp();
  const ROLES = ['Owner','Shop Foreman','Lead Welder','Welder','Fitter','Detailer','Paint/Finish','Helper'];
  const COLORS = ['#1e3a5f','#f97316','#0891b2','#d97706','#059669','#7c3aed','#be185d','#dc2626'];

  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'Welder', color: COLORS[0] });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    addEmployee(form);
    onClose();
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">Add Employee</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="field"><label>Full Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="First Last" required /></div>
            <div className="form-row">
              <div className="field"><label>Email</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="employee@yourshop.com" /></div>
              <div className="field"><label>Phone</label><input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 555-5555" /></div>
            </div>
            <div className="form-row">
              <div className="field">
                <label>Role</label>
                <select value={form.role} onChange={e => set('role', e.target.value)}>
                  {ROLES.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Color</label>
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  {COLORS.map(c => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => set('color', c)}
                      style={{
                        width: 24, height: 24, borderRadius: '50%', background: c, border: 'none', cursor: 'pointer',
                        outline: form.color === c ? '2px solid var(--t1)' : 'none',
                        outlineOffset: 2,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Add Employee</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InviteModal({ employee, onClose }) {
  const { inviteEmployee } = useApp();
  const [email, setEmail] = useState(employee?.email || '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    const res = await inviteEmployee({ email: email.trim(), role: 'employee' });
    setSending(false);
    if (res.ok) setSent(true);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">Invite to Clock-In App</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {sent ? (
            <div className="auth-success">✓ Invitation sent! The employee will receive a link to create their account and access the clock-in app.</div>
          ) : (
            <form onSubmit={handleSend}>
              <div className="field">
                <label>Employee Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="employee@yourshop.com" required />
              </div>
              <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 8 }}>
                They'll receive a magic link to create their account and access the SteelOps employee clock-in app. They'll only see the clock-in screen.
              </div>
              <div className="modal-foot" style={{ padding: 0, marginTop: 16, border: 'none' }}>
                <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={sending}>{sending ? 'Sending…' : 'Send Invite'}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function initialsFor(name) {
  return (name || '').split(/\s+/).filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '??';
}

export default function Team() {
  const { team, updateEmployee, deleteEmployee } = useApp();
  const [modal, setModal] = useState(null);

  const active   = team.filter(t => t.active !== false);
  const inactive = team.filter(t => t.active === false);

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Team</div>
          <div className="psub">{active.length} active employee{active.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="pacts">
          <button className="btn btn-primary" onClick={() => setModal({ type: 'add' })}>+ Add Employee</button>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <span className="card-title">Active Employees</span>
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>{active.length} employee{active.length !== 1 ? 's' : ''}</span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Clock-In App</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {active.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--t3)', padding: '24px' }}>No employees yet — add your first employee to get started.</td></tr>
            )}
            {active.map(emp => (
              <tr key={emp.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="emp-av" style={{ background: emp.color || 'var(--cyan)' }}>
                      {emp.initials || initialsFor(emp.name)}
                    </div>
                    <span style={{ fontWeight: 600 }}>{emp.name}</span>
                  </div>
                </td>
                <td>
                  <span className="bdg" style={{ background: 'var(--s3)', color: 'var(--t2)' }}>{emp.role}</span>
                </td>
                <td style={{ color: 'var(--t3)', fontSize: 12 }}>{emp.email || '—'}</td>
                <td style={{ color: 'var(--t3)', fontSize: 12 }}>{emp.phone || '—'}</td>
                <td>
                  {emp.userId
                    ? <span className="bdg" style={{ background: 'var(--teal-t)', color: 'var(--teal)' }}>✓ Invited</span>
                    : (
                      <button className="btn btn-ghost btn-sm" onClick={() => setModal({ type: 'invite', data: emp })}>
                        Send Invite
                      </button>
                    )
                  }
                </td>
                <td>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--rose)' }}
                    onClick={() => { if (window.confirm(`Remove ${emp.name}?`)) deleteEmployee(emp.id); }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {inactive.length > 0 && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">Inactive</span>
            <span style={{ fontSize: 12, color: 'var(--t3)' }}>{inactive.length}</span>
          </div>
          <table className="tbl">
            <tbody>
              {inactive.map(emp => (
                <tr key={emp.id} style={{ opacity: 0.6 }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="emp-av" style={{ background: emp.color || '#94a3b8', width: 28, height: 28, fontSize: 11 }}>
                        {emp.initials || initialsFor(emp.name)}
                      </div>
                      <span>{emp.name}</span>
                    </div>
                  </td>
                  <td style={{ color: 'var(--t3)', fontSize: 12 }}>{emp.role}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => updateEmployee(emp.id, { active: true })}>Reactivate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal?.type === 'add'    && <AddEmployeeModal onClose={() => setModal(null)} />}
      {modal?.type === 'invite' && <InviteModal employee={modal.data} onClose={() => setModal(null)} />}
    </div>
  );
}
